import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';

import {
  createTutorial,
  updateTutorial,
  TutorialAudience,
  TutorialDependency,
  MaxVideoSizeMB,
} from '../../services/TutorialService';
import { formatFileSize } from '../../utils/DiactiveUtils';

const SOFT_WARNING_MB = 50;

const TutorialFormDialog = ({
  open,
  mode,
  audience,
  tutorial,
  nextOrder,
  onClose,
  onSaved,
}) => {
  const fileInputRef = useRef(null);
  const [title, setTitle] = useState('');
  const [requiresAppAccess, setRequiresAppAccess] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isEdit = mode === 'edit';
  const isPatientAudience = audience === TutorialAudience.Patient;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setUploadProgress(0);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (isEdit && tutorial) {
      setTitle(tutorial.title || '');
      setRequiresAppAccess(tutorial.dependency === TutorialDependency.HasAppAccess);
    }
    else {
      setTitle('');
      setRequiresAppAccess(isPatientAudience);
    }
  }, [open, isEdit, tutorial, isPatientAudience]);

  const fileSizeMb = file ? file.size / (1024 * 1024) : 0;
  const showSoftWarning = file && fileSizeMb > SOFT_WARNING_MB && fileSizeMb <= MaxVideoSizeMB;

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setError(null);
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('El título es obligatorio');
      return;
    }
    if (!isEdit && !file) {
      setError('Se requiere un archivo de vídeo');
      return;
    }

    setSubmitting(true);
    setError(null);
    setUploadProgress(0);

    try {
      if (isEdit) {
        await updateTutorial(tutorial.id, {
          title: trimmedTitle,
          dependency: isPatientAudience
            ? (requiresAppAccess ? TutorialDependency.HasAppAccess : null)
            : undefined,
          file: file || undefined,
          onProgress: file ? setUploadProgress : undefined,
        });
      }
      else {
        await createTutorial({
          title: trimmedTitle,
          audience,
          order: nextOrder,
          dependency: isPatientAudience && requiresAppAccess
            ? TutorialDependency.HasAppAccess
            : undefined,
          file,
          onProgress: setUploadProgress,
        });
      }
      onSaved();
      onClose();
    }
    catch (err) {
      setError(err.message || 'Error al guardar el tutorial');
    }
    finally {
      setSubmitting(false);
    }
  };

  const audienceLabel = audience === TutorialAudience.Doctor ? 'médicos' : 'pacientes';

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="tutorial-form-dialog-title"
    >
      <DialogTitle id="tutorial-form-dialog-title">
        {isEdit ? 'Editar tutorial' : `Nuevo tutorial (${audienceLabel})`}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Título"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          disabled={submitting}
          sx={{ mb: 2, mt: 1 }}
        />

        {isPatientAudience && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresAppAccess}
                  onChange={(e) => setRequiresAppAccess(e.target.checked)}
                  disabled={submitting}
                />
              }
              label="Solo para pacientes con acceso a la app (Diactive-1)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ pl: 4, pr: 1 }}>
              Si está activado, este tutorial solo aparecerá para pacientes que tengan acceso a la
              aplicación móvil Diactive-1. Los pacientes sin acceso a la app no lo verán.
            </Typography>
          </Box>
        )}

        <Button variant="outlined" component="label" disabled={submitting} fullWidth>
          {isEdit ? 'Reemplazar vídeo (opcional)' : 'Seleccionar vídeo'}
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            hidden
            onChange={handleFileChange}
          />
        </Button>

        {file && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {file.name} — {formatFileSize(file.size)}
          </Typography>
        )}

        {showSoftWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            El vídeo supera {SOFT_WARNING_MB} MB. Los usuarios con conexión lenta tardarán más en
            cargarlo. Considera comprimirlo antes de subirlo (máximo {MaxVideoSizeMB} MB).
          </Alert>
        )}

        {submitting && (file || !isEdit) && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress * 100} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Subiendo… {Math.round(uploadProgress * 100)}%
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialFormDialog;
